// Codebook generation — the human/machine-readable description of every export
// column. This is the reproducibility backbone: a clean dataset is only usable
// in R/SPSS together with a codebook that decodes the integer-coded fields.
//
// Pure module (no DOM). Covers the flat DB column contract plus the derived
// analysis variables, so a frozen dataset can always be re-interpreted.

import { csvCell } from "./util";
import {
  ANALYSIS_COLUMNS,
  DERIVED_MAP,
  SECTIONS,
  getFieldMeta,
  naCodeFor,
  parseChoices,
  type FieldDef,
} from "./schema";

export interface CodebookEntry {
  column: string;
  label: string;
  section: string;
  type: FieldDef["type"] | "unknown";
  derived: boolean;
  /** N/A sentinel for this field (99 categorical, -99 quantitative, N/A text). */
  naCode: string;
  description: string;
  /** Decoded choices for coded fields, e.g. [{ value: "1", label: "DCB" }]. */
  codes: { value: string; label: string }[];
}

const DERIVED_SECTION = "Производные переменные (анализ)";

/** Map each column id to the title of the form section it belongs to. */
function buildSectionIndex(): Map<string, string> {
  const index = new Map<string, string>();
  SECTIONS.forEach((section) => {
    section.fields.forEach((field) => index.set(field.id, section.title));
  });
  return index;
}

/** Strip the trailing " — " label prefix that parseChoices adds, keeping the
 *  raw decoded meaning (e.g. "1 — DCB" -> "DCB"). */
function decodeChoices(hint: string): { value: string; label: string }[] {
  return parseChoices(hint).map((choice) => {
    const dash = choice.label.indexOf("—");
    const label = dash >= 0 ? choice.label.slice(dash + 1).trim() : choice.label;
    return { value: choice.value, label };
  });
}

/** Build a codebook entry for every analysis column (flat + derived). */
export function buildCodebook(): CodebookEntry[] {
  const sectionIndex = buildSectionIndex();
  return ANALYSIS_COLUMNS.map((column) => {
    const meta = getFieldMeta(column);
    const derived = DERIVED_MAP.has(column);
    const type = meta?.type ?? "unknown";
    const codes = meta && type === "select" ? decodeChoices(meta.hint) : [];
    return {
      column,
      label: meta?.label ?? column,
      section: derived ? DERIVED_SECTION : sectionIndex.get(column) ?? "—",
      type,
      derived,
      naCode: meta ? naCodeFor(meta) : "",
      description: meta?.hint ?? "",
      codes,
    };
  });
}

/** Flatten coded choices into a single "1=DCB | 2=FMJ" string for tabular use. */
function codesToText(codes: { value: string; label: string }[]): string {
  return codes.map((code) => `${code.value}=${code.label}`).join(" | ");
}

/** Codebook as a semicolon-separated CSV (same dialect as the data export).
 *  Prepends UTF-8 BOM so Excel (RU locale) reads it as UTF-8 and doesn't
 *  mangle the Cyrillic labels/descriptions into Windows-1251 mojibake. */
export function codebookToCsv(entries: CodebookEntry[] = buildCodebook()): string {
  const header = ["Column", "Label", "Section", "Type", "Derived", "NA_Code", "Description", "Codes"];
  const lines = [header.join(";")];
  entries.forEach((entry) => {
    lines.push(
      [
        entry.column,
        entry.label,
        entry.section,
        entry.type,
        entry.derived ? "1" : "0",
        entry.naCode,
        entry.description,
        codesToText(entry.codes),
      ]
        .map(csvCell)
        .join(";"),
    );
  });
  return "﻿" + lines.join("\n");
}

/** Codebook as readable Markdown, grouped by section (for the docs/wiki). */
export function codebookToMarkdown(entries: CodebookEntry[] = buildCodebook()): string {
  const order: string[] = [];
  const grouped = new Map<string, CodebookEntry[]>();
  entries.forEach((entry) => {
    if (!grouped.has(entry.section)) {
      grouped.set(entry.section, []);
      order.push(entry.section);
    }
    grouped.get(entry.section)!.push(entry);
  });

  const blocks = order.map((section) => {
    const rows = grouped
      .get(section)!
      .map((entry) => {
        const codes = entry.codes.length ? codesToText(entry.codes) : entry.description;
        return `| \`${entry.column}\` | ${entry.label} | ${entry.type}${entry.derived ? " (derived)" : ""} | ${codes} |`;
      })
      .join("\n");
    return `## ${section}\n\n| Колонка | Название | Тип | Значения / описание |\n|---|---|---|---|\n${rows}`;
  });

  const naNote = [
    "## Кодировка пропусков (N/A)",
    "",
    "Процедура не проводилась → ячейка кодируется сентинелом, а не остаётся пустой:",
    "- категориальные (select) → `99`;",
    "- количественные (number/formula) → `-99` (биологически невозможно);",
    "- текст → `N/A`.",
    "",
    "Пустая ячейка означает только «данные должны быть, но ещё не внесены». В R/SPSS",
    "объявите пропущенными: `99` для категориальных и `-99`/`-999` для количественных",
    "(колонка `NA_Code` в CSV-codebook). Late loss (`LLL_FU`) может быть законно",
    "отрицательным — поэтому пропуск помечается точным `-99`, а не любым `< 0`.",
  ].join("\n");
  return `# Codebook — MedicX CRF\n\nРасшифровка всех ${entries.length} колонок аналитического датасета (плоские DB-колонки + производные). Генерируется из \`src/schema.ts\`; экспортируется вместе с clean dataset.\n\n${naNote}\n\n${blocks.join("\n\n")}\n`;
}
